import { useAccessToken } from '@salesforce/commerce-sdk-react';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query/src/types';
import { useSearchParams } from '../../hooks/useSearchParams';
import { QueryKey } from '../../utils/api';
import { isServer } from '../../utils/utils';
import { bffGet } from '../bff';
import { useStore } from './useStore';
import type { UserAddress } from '../../hooks/model/UserAddress';
import type { Store } from './model';

export const useNearestStoreByLocation = (
    userAddress?: UserAddress,
): UseQueryResult<Store | null> => {
    const { getTokenWhenReady } = useAccessToken();
    const params = useSearchParams();

    // Kiosk machines use location_id param to identify the store.
    const storeId = params.get('location_id');
    const { data: store, isFetching } = useStore(storeId ?? undefined);

    return useQuery<unknown, unknown, Store>({
        queryFn: async () => {
            if (!isServer) await window.sfccSessionCreationPromise;

            const accessToken = await getTokenWhenReady();

            if (store) {
                return store;
            }

            if (!userAddress || isFetching) {
                return null;
            }

            const { latitude, longitude } = userAddress;
            try {
                return await bffGet<Store>('/stores/search/nearest', {
                    httpParams: [{ latitude, longitude }],
                    accessToken,
                });
            } catch (e) {
                return null;
            }
        },
        queryKey: [
            QueryKey.GET_NEAREST_STORE_BY_LOCATION,
            JSON.stringify({
                storeId,
                isFetching,
                latitude: userAddress?.latitude,
                longitude: userAddress?.longitude,
            }),
        ],
        keepPreviousData: true,
        enabled: !isServer && !!userAddress,
        staleTime: Infinity,
    });
};
